import { Checkbox, Select } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useFormikContext } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BaseLoader from 'components/atoms/base-loader';
import { usStates } from 'constants/states';
import { STATE_ABBREVIATIONS } from 'constants/stateAbbreviations';
import { useClinicsForPod } from 'utils/hooks/pods/useClinicsForPod';

import ClinicAssignmentList from './clinic-assignment-list';
import parentS from '../styles.module.scss';
import s from './styles.module.scss';

import type {
  ICreatePodFormValues,
  IClinicFormValue,
} from '../validation-schema';
import type { FC } from 'react';

const stateOptions = usStates.map((state) => ({
  label: `${state.label} (${STATE_ABBREVIATIONS[state.value] ?? ''})`,
  value: state.value,
}));

const AssignStateClinicSection: FC = () => {
  const { t } = useTranslation();
  const { values, setFieldValue, errors, touched, setFieldTouched } =
    useFormikContext<ICreatePodFormValues>();
  const { clinics: allClinics, isLoading: clinicsLoading } = useClinicsForPod();
  const [clinicSelectKey, setClinicSelectKey] = useState(0);
  const [outOfStateWarning, setOutOfStateWarning] = useState<{
    clinicGuid: string;
    stateName: string;
  } | null>(null);

  // Auto-clear stale out-of-state warning when form state changes:
  // - Pod type switches away from RPM (CCM has no state restrictions)
  // - The auto-added state is manually removed from the states dropdown
  useEffect(() => {
    if (!outOfStateWarning) return;
    if (
      values.podType !== 'RPM' ||
      !values.states.includes(outOfStateWarning.stateName)
    ) {
      setOutOfStateWarning(null);
    }
  }, [values.podType, values.states, outOfStateWarning]);

  const selectedGuids = useMemo(
    () => new Set(values.clinicGuids.map((c) => c.guid)),
    [values.clinicGuids],
  );

  const clinicOptions = useMemo(() => {
    let filtered = allClinics;

    if (values.podType === 'RPM' && values.states.length > 0) {
      const stateSet = new Set(values.states);
      filtered = filtered.filter((c) => stateSet.has(c.state));
    }

    return filtered
      .filter((c) => !selectedGuids.has(c.guid))
      .map((c) => ({
        label: (
          <div className={s.clinicOption}>
            <span>{c.name}</span>
            <span className={s.clinicOptionMeta}>
              {c.state} &middot; {c.patientCount} pts
            </span>
          </div>
        ),
        value: c.guid,
        searchText: c.name,
      }));
  }, [allClinics, values.podType, values.states, selectedGuids]);

  const handleClinicSelect = useCallback(
    (guid: string) => {
      const clinic = allClinics.find((c) => c.guid === guid);
      if (!clinic) return;

      const clinicValue: IClinicFormValue = {
        guid: clinic.guid,
        name: clinic.name,
        state: clinic.state,
        patientCount: clinic.patientCount,
      };

      setFieldValue('clinicGuids', [...values.clinicGuids, clinicValue]);

      // Auto-expand: add clinic's state to pod states if not already present (RPM only)
      if (values.podType === 'RPM' && clinic.state) {
        // Resolve abbreviation to full name if needed (e.g. "CA" -> "California")
        let resolvedState = clinic.state;
        if (!STATE_ABBREVIATIONS[clinic.state]) {
          // clinic.state might be an abbreviation — try reverse lookup
          const fullName = Object.entries(STATE_ABBREVIATIONS).find(
            ([, abbr]) => abbr === clinic.state,
          )?.[0];
          if (fullName) {
            resolvedState = fullName;
          } else {
            // Unknown/invalid state — don't auto-add
            resolvedState = '';
          }
        }
        if (resolvedState && !values.states.includes(resolvedState)) {
          setFieldValue('states', [...values.states, resolvedState]);
          setOutOfStateWarning({
            clinicGuid: clinic.guid,
            stateName: resolvedState,
          });
        }
      }

      // Increment key to remount the Select, resetting it to show placeholder
      setClinicSelectKey((k) => k + 1);
    },
    [
      allClinics,
      values.clinicGuids,
      values.podType,
      values.states,
      setFieldValue,
    ],
  );

  const handleRemoveClinic = (guid: string) => {
    setFieldValue(
      'clinicGuids',
      values.clinicGuids.filter((c) => c.guid !== guid),
    );
    if (outOfStateWarning && guid === outOfStateWarning.clinicGuid) {
      setOutOfStateWarning(null);
    }
  };

  const statesError = touched.states ? (errors.states as string) : undefined;
  const clinicGuidsError = touched.clinicGuids
    ? (errors.clinicGuids as string)
    : undefined;

  return (
    <div className={s.section} data-cy="create-pod__assign-state-clinic">
      <div className={parentS.sectionHeader}>
        <span className={parentS.sectionTitle}>
          {values.podType === 'RPM'
            ? t('labels.assignStateClinic')
            : t('labels.assignClinicTitle')}
        </span>
        <span className={parentS.sectionHelper}>
          {values.podType === 'RPM'
            ? t('labels.assignStateClinicHelper')
            : t('labels.assignClinicHelper')}
        </span>
      </div>

      <div style={{ display: values.podType === 'RPM' ? 'block' : 'none' }}>
        <div className={s.fieldLabel}>
          <span className={s.fieldLabelText}>{t('labels.podState')}</span>
          <span className={s.requiredMark}>*</span>
        </div>
        <Select
          mode="multiple"
          optionLabelProp="value"
          placeholder={t('labels.searchStates')}
          value={values.states}
          onChange={(newStates: string[]) => setFieldValue('states', newStates)}
          onBlur={() => setFieldTouched('states', true)}
          options={stateOptions}
          showSearch
          allowClear
          popupClassName={`${parentS.selectDropdown} ${s.stateDropdown}`}
          filterOption={(input, option) =>
            (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
          }
          menuItemSelectedIcon={null}
          optionRender={(option) => (
            <div className={s.checkboxOption}>
              <Checkbox
                checked={values.states.includes(option.value as string)}
              />
              <span>{option.label}</span>
            </div>
          )}
          style={{ width: '100%' }}
          data-cy="create-pod__pod-state"
        />
        {statesError && <span className={s.errorText}>{statesError}</span>}
      </div>

      <div aria-live="polite" className={s.ariaLiveRegion}>
        {values.podType === 'RPM'
          ? t('labels.stateSelectionAvailableForRpm')
          : t('labels.stateSelectionNotRequiredForCcm')}
      </div>

      <div>
        <div className={s.fieldLabel}>
          <span className={s.fieldLabelText}>{t('labels.assignClinic')}</span>
          <span className={s.requiredMark}>*</span>
        </div>
        <Select
          key={clinicSelectKey}
          showSearch
          popupClassName={parentS.selectDropdown}
          loading={clinicsLoading}
          placeholder={t('labels.pleaseSelect')}
          onSelect={handleClinicSelect}
          options={clinicOptions}
          filterOption={(input, option) =>
            ((option as { searchText?: string })?.searchText ?? '')
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          notFoundContent={
            clinicsLoading ? (
              <BaseLoader loading height={20} width={20} />
            ) : undefined
          }
          style={{ width: '100%' }}
          data-cy="create-pod__assign-clinic"
        />
        {clinicGuidsError && (
          <span className={s.errorText}>{clinicGuidsError}</span>
        )}
      </div>

      {outOfStateWarning && values.podType === 'RPM' && (
        <div
          className={s['out-of-state-warning']}
          role="alert"
          aria-live="assertive"
          data-cy="out-of-state-clinic-warning"
        >
          <ExclamationCircleOutlined />
          <span>
            {t('labels.outOfStateClinicWarning', {
              state: outOfStateWarning.stateName,
            })}
          </span>
        </div>
      )}

      {values.clinicGuids.length > 0 && (
        <ClinicAssignmentList
          clinics={values.clinicGuids}
          onRemove={handleRemoveClinic}
        />
      )}
    </div>
  );
};

export default AssignStateClinicSection;
